import { Result } from '@/classes/result';
import { OrganizationRole } from '@/enums/roles';
import { ErrorMessage } from '@/messages/error';
import { useAuthStore } from '@/stores/auth';
import type { Team, TeamForm } from '@/types/team';
import { callApi } from '@/utils/api';
import { handleAxiosError } from '@/utils/error';
import toast from '@/utils/toast';
import { defineStore } from 'pinia';
import { ref } from 'vue';

export const useTeamStore = defineStore('teams', () => {
    const teams = ref<Team[]>([]);

    async function fetch(): Promise<Team[]> {
        try {
            const result = await callApi({
                method: 'GET',
                endpoint: `teams`
            });

            return result.object as Team[];
        } catch (error) {
            return handleAxiosError(error, [], 'Teams konden niet opgehaald worden');
        }
    }

    async function create(newTeam: TeamForm): Promise<Team> {
        try {
            const result = await callApi({
                method: 'POST',
                endpoint: `teams`,
                body: newTeam
            });

            toast.success('TeamCreated');
            return result.object as Team;
        } catch (error) {
            return handleAxiosError(error, [], ErrorMessage.TEAM_CREATION_FAILED);
        }
    }

    async function update(team: TeamForm): Promise<Result> {
        try {
            const result = await callApi({
                method: 'PUT',
                endpoint: `teams/${team._id}`,
                body: team
            });

            toast.success('TeamUpdated');
            return result;
        } catch (error) {
            return handleAxiosError(error, [], ErrorMessage.TEAM_UPDATE_FAILED);
        }
    }

    async function fetchById(id: string): Promise<Team> {
        try {
            const result = await callApi({
                method: 'GET',
                endpoint: `teams/${id}`
            });

            return result.object as Team;
        } catch (error) {
            return handleAxiosError(error, [], 'Team kon niet opgehaald worden');
        }
    }

    async function fetchTeamBasedOnRole(): Promise<void> {
        const authStore = useAuthStore();
        if (authStore.isSuperAdmin()) {
            teams.value = await fetch();
            return;
        }

        if (authStore.isAuthorized([OrganizationRole.Admin], authStore.getCurrentOrganization())) {
            teams.value = await getEmployeeAndOrganizationTeams(authStore.getLoggedInEmployee()._id, authStore.getCurrentOrganization()._id);
            return;
        }

        teams.value = await getEmployeeTeams(authStore.getLoggedInEmployee()._id);
    }

    async function getEmployeeAndOrganizationTeams(employeeId: string, organizationId: string): Promise<Team[]> {
        try {
            const result = await callApi({
                method: 'GET',
                endpoint: `teams/organization/${organizationId}/containing_employee/${employeeId}`
            });

            return result.object as Team[];
        } catch (error) {
            return handleAxiosError(error, [], ErrorMessage.EMPLOYEE_TEAMS_FETCH_FAILED);
        }
    }

    async function getEmployeeTeams(employeeId: string): Promise<Team[]> {
        try {
            const result = await callApi({
                method: 'GET',
                endpoint: `teams/containing_employee/${employeeId}`
            });

            return result.object as Team[];
        } catch (error) {
            return handleAxiosError(error, [], ErrorMessage.EMPLOYEE_TEAMS_FETCH_FAILED);
        }
    }

    async function removeEmployeeFromTeam(teamId: string, employeeId: string): Promise<void> {
        try {
            await callApi({
                method: 'DELETE',
                endpoint: `teams/${teamId}/employee/${employeeId}`
            });

            const team = teams.value.find((t) => t._id === teamId);
            if (team != null) team.employees = team?.employees.filter((employee) => employee._id !== employeeId);

            toast.success('EmployeeRemovedFromTeam');
        } catch (error) {
            return handleAxiosError(error, [], ErrorMessage.EMPLOYEE_REMOVAL_FAILED);
        }
    }

    async function deleteById(id: string): Promise<void> {
        try {
            await callApi({ method: 'DELETE', endpoint: `teams/${id}` });

            teams.value = teams.value.filter((team) => team._id !== id);
            toast.success('TeamDeleted');
        } catch (error) {
            return handleAxiosError(error, [], 'Team kon niet verwijderd worden');
        }
    }

    return {
        fetch,
        create,
        fetchById,
        removeEmployeeFromTeam,
        deleteById,
        fetchTeamBasedOnRole,
        teams,
        update
    };
});
